import React, { useCallback } from 'react';
import { Form, Formik } from 'formik';
import { IAdminUserData } from '../../../types/administration/userAdministration/IAdminUser';
import { editAdminUserFormValidator } from '../../../validation/administration/EditAdminUserFormValidator';
import { EditAdminUserFormFields } from './EditAdminUserFormFields';
import { UUID } from '../../../types/shared/UUID';
import { SlideOver } from '../../../tailwindComponents/slideOver/SlideOver';
import { useUpdateIdentityMutation } from '../../../API/administration/IdentityAPI';
import { useUpdateAdminUserMutation } from '../../../API/administration/AdminUsersAPI';
import { checkForMutationSuccess } from '../../../API/checkForMutationSuccess';
import _ from 'lodash';
import { SubmissionFooter } from '../../userProfile/formElements/SubmissionFooter';
import { ErrorMessageTW } from '../../../tailwindComponents/errorMessage/ErrorMessageTW';

interface EditAdminUserFormState {
  fullName: string;
  email: string;
  roles: UUID[];
}

export const EditAdminUserForm: React.FC<{
  adminUser: IAdminUserData | null;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ adminUser, open, setOpen }) => {
  const [updateIdentity, { error: updateIdentityError, isLoading: updateIdentityIsLoading }] =
    useUpdateIdentityMutation();
  const [updateAdminUser, { error: updateAdminUserError, isLoading: updateAdminUserIsLoading }] =
    useUpdateAdminUserMutation();
  
  const initialValues: EditAdminUserFormState = {
    fullName: adminUser?.fullName ?? '',
    email: adminUser?.email ?? '',
    roles: adminUser?.identity?.roles.map(({ id }) => id) ?? [],
  };
  
  const analyzeAndSubmit = useCallback(
    async (editAdminUserRequest: EditAdminUserFormState) => {
      if (!adminUser) {
        return;
      }

      let success = true;
      const hasUserDataChanged = 
        editAdminUserRequest.fullName !== adminUser.fullName || 
        editAdminUserRequest.email !== adminUser.email;
      
      if (hasUserDataChanged) {
        const updatePayload: any = {
          id: adminUser.id,
        };
        
        if (editAdminUserRequest.fullName !== adminUser.fullName) {
          updatePayload.fullName = editAdminUserRequest.fullName;
        }
        if (editAdminUserRequest.email !== adminUser.email) {
          updatePayload.email = editAdminUserRequest.email;
        }
        
        const response = await updateAdminUser(updatePayload);
        success &&= checkForMutationSuccess(response, 'updating Admin User');
      }
      const currRoles = adminUser.identity.roles.map(({ id }) => id);
      const roleIdsToAdd = _.difference(editAdminUserRequest.roles, currRoles);
      const roleIdsToRemove = _.difference(currRoles, editAdminUserRequest.roles);

      if (roleIdsToAdd.length + roleIdsToRemove.length > 0) {
        const response = await updateIdentity({
          id: adminUser.identity.id,
          roleIdsToAdd,
          roleIdsToRemove,
        });
        success &&= checkForMutationSuccess(response, 'updating Identity');
      }
      if (success) {
        setOpen(false);
      }
    },
    [adminUser, updateAdminUser, updateIdentity, setOpen]
  );

  return (
    <SlideOver open={open} setOpen={setOpen} title={'Edit Pinecone User'}>
      <ErrorMessageTW rtkError={updateAdminUserError ?? updateIdentityError} />
      <Formik
        initialValues={initialValues}
        onSubmit={analyzeAndSubmit}
        enableReinitialize
        validationSchema={editAdminUserFormValidator}
      >
        <Form>
          <div className={'flex flex-col gap-2'}>
            <EditAdminUserFormFields />
            <SubmissionFooter
              onCancel={() => setOpen(false)}
              disabled={updateAdminUserIsLoading || updateIdentityIsLoading}
            />
          </div>
        </Form>
      </Formik>
    </SlideOver>
  );
};
